import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { filter, map, Observable, tap } from 'rxjs';
import { LocalStorageService } from './local.storage.service';
import {
  BaseLoginRequest,
  CommonAuthLoginApiService,
  LoginByAppleCodeRequest,
  LoginByOAuthCodeRequest,
  LoginByOtpCodeRequest,
  LoginByPasswordRequest,
  PasswordRecoveryRequest,
  PlatformType,
  RegistrationConfirmRequest,
  RegistrationRequest,
  TokenResponse,
} from '@data-access';
import { environment } from '@shared-app-config';

@Injectable({
  providedIn: 'root',
})
export class AuthHttpService {
  private http = inject(HttpClient);
  private lsService = inject(LocalStorageService);
  private commonAuthLoginApiService = inject(CommonAuthLoginApiService);

  private featureUrl = `${environment.apiUrl}/login`;

  loginPassword(request: LoginByPasswordRequest) {
    const url = `${this.featureUrl}/password`;
    return this.http.post<TokenResponse>(url, request).pipe(
      tap((response) => {
        this.setAuthDataToLs(response, request.deviceId!);
      }),
    );
  }

  loginByOtpRequest(email: string, platformType?: string) {
    const url = `${this.featureUrl}/otp/request`;
    const request = { value: email };
    return this.http.post<TokenResponse>(url, request, {
      ...(platformType && { params: { platformType } }),
    });
  }

  loginByOtp(request: LoginByOtpCodeRequest) {
    const url = `${this.featureUrl}/otp`;
    return this.http.post<TokenResponse>(url, request).pipe(
      tap((response) => {
        this.setAuthDataToLs(response, request.deviceId!);
      }),
    );
  }

  loginByGoogleCode(request: LoginByOAuthCodeRequest) {
    const url = `${this.featureUrl}/google/code`;
    return this.http.post<TokenResponse>(url, request).pipe(
      tap((response) => {
        this.setAuthDataToLs(response, request.deviceId!);
      }),
    );
  }

  loginByAppleCode(request: LoginByAppleCodeRequest) {
    const url = `${this.featureUrl}/apple/code`;
    return this.http.post<TokenResponse>(url, request).pipe(
      tap((response) => {
        this.setAuthDataToLs(response, request.deviceId!);
      }),
    );
  }

  reLogin(refreshToken: string) {
    const url = `${this.featureUrl}/refresh/${refreshToken}`;
    return this.http.get<TokenResponse>(url).pipe(
      tap((response) => {
        this.setAuthDataToLs(response);
      }),
    );
  }

  registrationAvailable(email: string): Observable<boolean> {
    const url = `${this.featureUrl}/registration/available`;
    return this.http
      .post<{ value: boolean }>(url, { value: email })
      .pipe(map((response) => response.value));
  }

  registrationRequest(request: RegistrationRequest) {
    const url = `${this.featureUrl}/registration/request`;
    return this.http.post<string>(url, request);
  }

  registrationConfirm(request: RegistrationConfirmRequest) {
    const url = `${this.featureUrl}/registration/confirm`;
    return this.http.post<TokenResponse>(url, request).pipe(
      tap((response) => {
        this.setAuthDataToLs(response, request.deviceId!);
      }),
    );
  }

  checkRegistrationCode(code: string) {
    const url = `${this.featureUrl}/registration/request/check`;
    const request = { value: code };
    return this.http.post<void>(url, request);
  }

  recoveryRequest(email: string, platformType?: string) {
    const url = `${this.featureUrl}/recovery/request`;
    const request = { value: email };
    return this.http.post<void>(url, request, {
      ...(platformType && { params: { platformType } }),
    });
  }

  recoveryConfirm(request: PasswordRecoveryRequest) {
    const url = `${this.featureUrl}/recovery/confirm`;
    return this.http.post<TokenResponse>(url, request).pipe(
      tap((response) => {
        this.setAuthDataToLs(response, request.deviceId!);
      }),
    );
  }

  checkRecoveryCode(code: string) {
    const url = `${this.featureUrl}/recovery/request/check`;
    const request = { value: code };
    return this.http.post<void>(url, request);
  }

  getCabinetToken(deviceId: string, platformType: PlatformType) {
    const url = `${environment.apiUrl}/token/get`;
    const request = { deviceId, platformType };

    return this.http.post<TokenResponse>(url, request).pipe(
      tap((response) => {
        this.setAuthDataToLs(response);
      }),
    );
  }

  loginByQrCrToken(qrToken: string, body: BaseLoginRequest) {
    return this.commonAuthLoginApiService.loginByQrToken(qrToken, body).pipe(
      filter(Boolean),
      tap((response) => {
        this.setAuthDataToLs(response);
      }),
    );
  }

  private setAuthDataToLs(authData: TokenResponse, deviceId?: string) {
    this.lsService.set('accessToken', authData.token!);
    this.lsService.set('refreshToken', authData.refresh!);
    this.lsService.set('expiresIn', authData.validUntil!);
    deviceId && this.lsService.set('deviceId', deviceId);
  }
}
